"use client";

import { Suspense } from "react";
import type { CatalogContentBlock } from "@/domain/catalog";
import { BlockBoundary } from "@/app/features/shared/BlockBoundary";
import { pickBlockComponent } from "./helpers/content-blocks-map";
import {
	AboutTheProductBlock,
	type AboutTheProductResolved
} from "./blocks/AboutTheProduct";
import { RecipePairings } from "./blocks/RecipePairings";
import { UpsellSet, type UpsellSetData } from "./blocks/UpsellSet";
import type { NormalizedRecipe } from "./helpers/recipe-pairings/normalized-recipe";
import * as styles from "./ContentBlocksRenderer.module.scss";

export interface ContentBlocksRendererProps {
	blocks: CatalogContentBlock[];
	parentSku?: string;
	resolvedRecipePairings?: NormalizedRecipe[][];
	resolvedUpsellSets?: UpsellSetData[];
	resolvedAboutProduct?: AboutTheProductResolved;
}

export function ContentBlocksRenderer({
	blocks,
	parentSku,
	resolvedRecipePairings,
	resolvedUpsellSets,
	resolvedAboutProduct
}: ContentBlocksRendererProps) {
	if (!blocks?.length) return null;

	let recipePairingsOrdinal = 0;
	let upsellSetOrdinal = 0;

	return (
		<div
			className={styles["content-blocks-renderer"]}
			data-component="ContentBlocksRenderer"
		>
			{blocks.map((block, i) => {
				if (block.blockType === "recipe_pairings_block") {
					const recipes =
						resolvedRecipePairings?.[recipePairingsOrdinal++] ?? [];
					return (
						<BlockItem
							key={`${block.blockType}-${i}`}
							type={block.blockType}
						>
							<RecipePairings block={block} recipes={recipes} />
						</BlockItem>
					);
				}
				if (block.blockType === "upsell_set_block") {
					const resolved = resolvedUpsellSets?.[upsellSetOrdinal++];
					return (
						<BlockItem
							key={`${block.blockType}-${i}`}
							type={block.blockType}
						>
							<UpsellSet block={block} resolved={resolved} />
						</BlockItem>
					);
				}
				if (block.blockType === "about_product") {
					return (
						<BlockItem
							key={`${block.blockType}-${i}`}
							type={block.blockType}
						>
							<AboutTheProductBlock
								block={block}
								resolved={resolvedAboutProduct}
							/>
						</BlockItem>
					);
				}
				const Block = pickBlockComponent(block);
				return (
					<BlockItem
						key={`${block.blockType}-${i}`}
						type={block.blockType}
					>
						<Block block={block} parentSku={parentSku} />
					</BlockItem>
				);
			})}
		</div>
	);
}

function BlockItem({
	type,
	children
}: {
	type: string;
	children: React.ReactNode;
}) {
	return (
		<div
			data-block-boundary={type}
			className={styles["content-blocks-renderer__item"]}
		>
			<BlockBoundary type={type}>
				<Suspense fallback={<BlockSkeleton type={type} />}>
					{children}
				</Suspense>
			</BlockBoundary>
		</div>
	);
}

function BlockSkeleton({ type }: { type: string }) {
	return (
		<div
			role="status"
			aria-label={`Loading ${type}`}
			data-skeleton={type}
			className={styles["content-blocks-renderer__skeleton"]}
		/>
	);
}
